import {computed, ref} from 'vue';
import type {KeycloakConfig, KeycloakTokenParsed} from 'keycloak-js';
import Keycloak from 'keycloak-js';

import settings from '@/config/settings';

import type {ERoleSystem} from '@/api/enums/role';

import type {IKeycloakUser} from './types';

export const useKeycloakHook = (config?: KeycloakConfig) => {
  const isInit = ref<boolean>(false);
  const isInitError = ref<boolean>(false);
  const isAuthenticated = ref<boolean>(false);

  const token = ref<string | null>(null);
  const tokenParsed = ref<KeycloakTokenParsed | null>(null);

  const getConfig = computed(() => {
    return (
      config ?? {
        url: settings.SERVICES.KEYCLOAK.URL,
        realm: settings.SERVICES.KEYCLOAK.REALM,
        clientId: settings.SERVICES.KEYCLOAK.CLIENT_ID,
      }
    );
  });

  const getKeycloak = computed(() => new Keycloak(getConfig.value));
  const getToken = computed(() => token.value);
  const getIsInit = computed(() => {
    return isInit.value && (isAuthenticated.value ? !!getToken.value : true);
  });
  const getIsInitError = computed(() => isInitError.value);

  const getIsLoggedIn = computed(() => !!getToken.value);

  const getUserRoles = computed(() => {
    let roles = ['guest'];

    if (tokenParsed.value && tokenParsed.value.resource_access) {
      roles = tokenParsed.value.resource_access[
        settings.SERVICES.KEYCLOAK.CLIENT_ID_ROLES ?? 'memorial_back'
      ]?.roles ?? ['guest'];
    }

    return roles;
  });

  const getUserInfo = computed<IKeycloakUser>(() => {
    const user: IKeycloakUser = !!tokenParsed.value
      ? {
          id: tokenParsed.value.sub,
          name: tokenParsed.value.name ?? tokenParsed.value.email,
          email: tokenParsed.value.email,
          roles: getUserRoles.value as ERoleSystem[],
        }
      : {
          name: 'guest',
          email: 'guest@p13.com',
          roles: [],
        };

    return user;
  });

  const loadToken = async () => {
    token.value = getKeycloak.value.token ?? null;
    tokenParsed.value = getKeycloak.value.tokenParsed ?? null;
  };

  const signIn = async () => {
    await getKeycloak.value.login({
      redirectUri: `${document.location.href}`,
    });
  };

  const signOut = async () => await getKeycloak.value.logout();

  const init = async () => {
    getKeycloak.value.onTokenExpired = () => getKeycloak.value.updateToken(-1);
    getKeycloak.value.onAuthRefreshSuccess = () => loadToken();
    getKeycloak.value.onAuthRefreshError = () => location.reload();

    isAuthenticated.value = await getKeycloak.value
      .init({
        onLoad: 'check-sso',
      })
      .catch(() => (isInitError.value = true))
      .finally(() => (isInit.value = true));

    if (isInit.value && !isAuthenticated.value) await signIn();

    await loadToken();
  };

  return {
    getKeycloak,
    getIsLoggedIn,
    getIsInit,
    getIsInitError,

    getToken,
    getUserInfo,

    init,
    signIn,
    signOut,
  };
};

export const keycloakAPI = useKeycloakHook();

export default useKeycloakHook;
